import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { useEffect, useRef } from "react";

import ScrollToTop from "./components/ScrollToTop";
import ScrollToTopButton from "./components/ScrollToTopButton";
import CookieConsent from "./components/CookieConsent";

import { trackPageView, trackServiceView } from "@/utils/metaPixel";

import Index from "./pages/Index";
import Implantology from "./pages/Implantology";
import Endodontics from "./pages/Endodontics";
import OralSurgery from "./pages/OralSurgery";
import Periodontology from "./pages/Periodontology";
import Orthodontics from "./pages/Orthodontics";
import Bonding from "./pages/Bonding";
import Prosthetics from "./pages/Prosthetics";
import LipFiller from "./pages/LipFiller";
import Cariesology from "./pages/Cariesology";
import TeethWhitening from "./pages/TeethWhitening";
import DentalTourism from "./pages/DentalTourism";
import Prices from "./pages/Prices";
import RequestQuote from "./pages/RequestQuote";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";
import NotFound from "./pages/NotFound";

import LangLayout from "./routes/LangLayout";
import RedirectToLang from "./routes/RedirectToLang";
import { stripLangPrefix } from "@/utils/routeBuilders";
import { readConsentSettings } from "@/utils/consent";
import { initializeMetaPixel } from "@/utils/metaPixel";
import { initializeGtm, trackGtmPageView, updateGoogleConsent } from "@/utils/gtm";

const queryClient = new QueryClient();

const servicePages = {
  "/implantology": "Implantology",
  "/endodontics": "Endodontics",
  "/oral-surgery": "Oral Surgery",
  "/periodontology": "Periodontology",
  "/orthodontics": "Orthodontics",
  "/bonding": "Dental Bonding",
  "/prosthetics": "Dental Prosthetics",
  "/lip-filler": "Lip Filler",
  "/cariesology": "Cariesology",
  "/teeth-whitening": "Teeth Whitening",
  "/dental-tourism": "Dental Tourism",
};

const PageViewTracker = () => {
  const location = useLocation();
  const lastTrackedAnalyticsPathRef = useRef<string | null>(null);
  const lastTrackedMarketingPathRef = useRef<string | null>(null);

  useEffect(() => {
    const run = () => {
      const settings = readConsentSettings();
      const strippedPath = stripLangPrefix(location.pathname);

      // Advanced Consent Mode: always load GTM (cookieless pings when storage is denied).
      initializeGtm();
      updateGoogleConsent(settings);

      if (lastTrackedAnalyticsPathRef.current !== strippedPath) {
        lastTrackedAnalyticsPathRef.current = strippedPath;
        trackGtmPageView(strippedPath);
      }

      // Marketing consent gates Meta Pixel load + SPA PageView/service events.
      if (settings?.marketing) {
        initializeMetaPixel();

        if (lastTrackedMarketingPathRef.current !== strippedPath) {
          lastTrackedMarketingPathRef.current = strippedPath;
          trackPageView();

          const serviceName = servicePages[strippedPath as keyof typeof servicePages];
          if (serviceName) trackServiceView(serviceName);
        }
      }
    };

    run();
    const onConsentUpdated = () => run();
    window.addEventListener("consent:updated", onConsentUpdated);
    return () => window.removeEventListener("consent:updated", onConsentUpdated);
  }, [location.pathname]);

  return null;
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <ScrollToTop />
        <ScrollToTopButton />
        <CookieConsent />
        <PageViewTracker />
        <Routes>
          <Route path="/" element={<RedirectToLang />} />

          <Route path="/bg/*" element={<LangLayout lang="bg" />}>
            <Route index element={<Index />} />
            <Route path="implantology" element={<Implantology />} />
            <Route path="endodontics" element={<Endodontics />} />
            <Route path="oral-surgery" element={<OralSurgery />} />
            <Route path="periodontology" element={<Periodontology />} />
            <Route path="orthodontics" element={<Orthodontics />} />
            <Route path="bonding" element={<Bonding />} />
            <Route path="prosthetics" element={<Prosthetics />} />
            <Route path="lip-filler" element={<LipFiller />} />
            <Route path="cariesology" element={<Cariesology />} />
            <Route path="teeth-whitening" element={<TeethWhitening />} />
            <Route path="dental-tourism" element={<DentalTourism />} />
            <Route path="prices" element={<Prices />} />
            <Route path="request-quote" element={<RequestQuote />} />
            <Route path="privacy-policy" element={<PrivacyPolicy />} />
            <Route path="terms-of-service" element={<TermsOfService />} />
            <Route path="*" element={<NotFound />} />
          </Route>

          <Route path="/en/*" element={<LangLayout lang="en" />}>
            <Route index element={<Index />} />
            <Route path="implantology" element={<Implantology />} />
            <Route path="endodontics" element={<Endodontics />} />
            <Route path="oral-surgery" element={<OralSurgery />} />
            <Route path="periodontology" element={<Periodontology />} />
            <Route path="orthodontics" element={<Orthodontics />} />
            <Route path="bonding" element={<Bonding />} />
            <Route path="prosthetics" element={<Prosthetics />} />
            <Route path="lip-filler" element={<LipFiller />} />
            <Route path="cariesology" element={<Cariesology />} />
            <Route path="teeth-whitening" element={<TeethWhitening />} />
            <Route path="dental-tourism" element={<DentalTourism />} />
            <Route path="prices" element={<Prices />} />
            <Route path="request-quote" element={<RequestQuote />} />
            <Route path="privacy-policy" element={<PrivacyPolicy />} />
            <Route path="terms-of-service" element={<TermsOfService />} />
            <Route path="*" element={<NotFound />} />
          </Route>

          <Route path="/de/*" element={<LangLayout lang="de" />}>
            <Route index element={<Index />} />
            <Route path="implantology" element={<Implantology />} />
            <Route path="endodontics" element={<Endodontics />} />
            <Route path="oral-surgery" element={<OralSurgery />} />
            <Route path="periodontology" element={<Periodontology />} />
            <Route path="orthodontics" element={<Orthodontics />} />
            <Route path="bonding" element={<Bonding />} />
            <Route path="prosthetics" element={<Prosthetics />} />
            <Route path="lip-filler" element={<LipFiller />} />
            <Route path="cariesology" element={<Cariesology />} />
            <Route path="teeth-whitening" element={<TeethWhitening />} />
            <Route path="dental-tourism" element={<DentalTourism />} />
            <Route path="prices" element={<Prices />} />
            <Route path="request-quote" element={<RequestQuote />} />
            <Route path="privacy-policy" element={<PrivacyPolicy />} />
            <Route path="terms-of-service" element={<TermsOfService />} />
            <Route path="*" element={<NotFound />} />
          </Route>

          <Route path="/gr/*" element={<LangLayout lang="gr" />}>
            <Route index element={<Index />} />
            <Route path="implantology" element={<Implantology />} />
            <Route path="endodontics" element={<Endodontics />} />
            <Route path="oral-surgery" element={<OralSurgery />} />
            <Route path="periodontology" element={<Periodontology />} />
            <Route path="orthodontics" element={<Orthodontics />} />
            <Route path="bonding" element={<Bonding />} />
            <Route path="prosthetics" element={<Prosthetics />} />
            <Route path="lip-filler" element={<LipFiller />} />
            <Route path="cariesology" element={<Cariesology />} />
            <Route path="teeth-whitening" element={<TeethWhitening />} />
            <Route path="dental-tourism" element={<DentalTourism />} />
            <Route path="prices" element={<Prices />} />
            <Route path="request-quote" element={<RequestQuote />} />
            <Route path="privacy-policy" element={<PrivacyPolicy />} />
            <Route path="terms-of-service" element={<TermsOfService />} />
            <Route path="*" element={<NotFound />} />
          </Route>

          <Route path="*" element={<NotFound />} />
        </Routes>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
